import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Search, ChevronDown, UserCircle2, Building2, Shield, Plus, SlidersHorizontal } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { CommanServices, normalizeIncidentRecipientDirectory } from '@/services/commanServices';
import { ApiEndPoint } from '@/services/apiUttiLities';
import { parseTimestamp } from '@/uitilites/utility';
import { COMPLETE_EVENT } from '@/context/DutyCheckJobContext';
import PersonnelAvailabilityFilters from './PersonnelAvailabilityFilters';
import PersonnelAvailabilitySummary from './PersonnelAvailabilitySummary';
import {
  DEFAULT_PERSONNEL_AVAILABILITY_FILTERS,
  dutyStatusMeta,
  dutyRank,
  mobilePresenceMeta,
  passesPersonnelAvailabilityFilters,
  readinessLocation,
  readinessRank,
  readinessStatusMeta,
  usableReadinessDistance,
} from './personnelAvailability';

const POLL_DUTY_MS = 15000;

// Shared directory + duty caches. Short TTL so newly added members / fresh duty
// checks appear without a page reload.
const CACHE_TTL_MS = 10000;
const directoryCache = new Map();
const directoryCacheKey = ({ incidentId = null, restricted = false } = {}) => {
  if (!restricted || !incidentId) return 'global:admin';
  let userId = 'anonymous';
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    userId = String(user?.id || user?._id || 'anonymous');
  } catch (_) { /* keep anonymous cache partition */ }
  return `incident:${incidentId}:user:${userId}`;
};
const loadDirectory = ({ incidentId = null, restricted = false, force = false } = {}) => {
  const key = directoryCacheKey({ incidentId, restricted });
  const cached = directoryCache.get(key);
  if (!force && cached?.promise && Date.now() - cached.at <= CACHE_TTL_MS) return cached.promise;

  const promise = restricted && incidentId
    ? CommanServices.getIncidentRecipientDirectory(incidentId)
      .then(normalizeIncidentRecipientDirectory)
      .catch(() => ({ users: [], departments: [], crews: [], duty: {} }))
    : Promise.all([
      CommanServices.getAppUsers().catch(() => ({ data: [] })),
      CommanServices.departments().catch(() => ({ data: [] })),
      CommanServices.getTeamsData().catch(() => null),
      axios.get(`${ApiEndPoint}/api/duty-status`, { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }).then((r) => r.data?.data?.users || {}).catch(() => ({})),
    ]).then(([u, d, t, duty]) => {
      const users = Array.isArray(u?.data) ? u.data : [];
      const departments = Array.isArray(d?.data) ? d.data : [];
      let teams = t?.data || t || [];
      if (!Array.isArray(teams)) teams = teams?.teams || [];
      const crews = [];
      teams.forEach((tm) => (tm.crews || []).forEach((c) => crews.push({
        ...c,
        team_id: c.team_id || tm.id,
        team_name: tm.team_name,
      })));
      return { users, departments, crews, duty };
    });
  directoryCache.set(key, { at: Date.now(), promise });
  return promise;
};

// Current members of a department/crew (used for the expandable member lists
// AND by resolveRecipients so both always agree).
const membersOfGroup = (dir, type, id) => {
  if (type === 'department') {
    const d = dir.departments.find((x) => String(x.id) === String(id));
    const ids = new Set((d?.members || []).map((m) => (
      typeof m === 'object' ? String(m.id || m.user_id || '') : String(m || '')
    )).filter(Boolean));
    const departmentRefs = new Set([d?.id, d?.department_name].filter(Boolean).map(String));
    return dir.users.filter((u) => {
      const profileDepartments = [
        ...(Array.isArray(u.departments_units) ? u.departments_units : []),
        u.department_unit,
        u.department,
      ].filter(Boolean).map(String);
      return u.is_active !== false && (
        ids.has(String(u.id))
        || profileDepartments.some((ref) => departmentRefs.has(ref))
      );
    });
  }
  const c = dir.crews.find((x) => String(x.id) === String(id));
  const refs = new Set();
  (c?.escalation_rules || []).forEach((rule) => {
    (Array.isArray(rule.escalated_to) ? rule.escalated_to : [rule.escalated_to]).forEach((t) => t && refs.add(String(t).toLowerCase()));
  });
  return dir.users.filter((u) => u.is_active !== false && (
    String(u.crew_id || '') === String(id)
    || refs.has(String(u.id).toLowerCase())
    || refs.has(String(u.email || '').toLowerCase())
    || refs.has(String(u.user_account || u.name || '').toLowerCase())
  ));
};

const DUTY_WINDOWS = [
  { value: null, label: 'Any time' },
  { value: 5, label: '5m' },
  { value: 15, label: '15m' },
  { value: 30, label: '30m' },
  { value: 60, label: '1h' },
  { value: 120, label: '2h' },
  { value: 360, label: '6h' },
  { value: 720, label: '12h' },
  { value: 1440, label: '24h' },
];

const NOTIFICATION_PRESETS = [
  {
    id: 'smart',
    label: '✦ Smart',
    filters: { seenWithin: 60, gps: 'usable', maxDistance: 5000, maxEta: 30 },
  },
  { id: 'ready', label: 'Ready now', filters: { incident: 'in_duty' } },
  { id: 'duty', label: 'On duty', filters: { duty: 'in_duty' } },
  { id: 'online', label: 'Online', filters: { app: 'online' } },
  { id: 'nearby', label: 'Nearby ≤ 5km', filters: { maxDistance: 5000 } },
];

const READINESS_BADGE_CLASSES = {
  ready: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  pending: 'border-amber-200 bg-amber-50 text-amber-700',
  danger: 'border-rose-200 bg-rose-50 text-rose-700',
  muted: 'border-slate-200 bg-slate-50 text-slate-600',
};

// Recipient picker for + card creation on REAL incidents: Users / Departments /
// Crews tabs with duty badges, duty-status + on-duty-recency filters, and
// expandable department/crew member lists with per-member picks or "Add all".
// Reports selections up via onChange; resolveRecipients() flattens
// departments/crews to their current member users.
// `involvedUserIds` (optional): when provided, rows are grouped under two
// headers — people/groups already involved in the incident first, then the
// rest of the system.
const RecipientPicker = ({
  selections,
  onChange,
  testIdPrefix = 'rp',
  involvedUserIds = null,
  incidentId = null,
  onlineUserIds = null,
  presenceByUserId = {},
  variant = 'default',
  allowedUserIds = null,
  allowedDepartmentIds = null,
  allowedTeamIds = null,
  allowedCrewIds = null,
}) => {
  const [dir, setDir] = useState({ users: [], departments: [], crews: [], duty: {} });
  const [incidentReadiness, setIncidentReadiness] = useState({});
  const [readinessLoadState, setReadinessLoadState] = useState('loaded');
  const [tab, setTab] = useState('users');
  const [search, setSearch] = useState('');
  const [availabilityFilters, setAvailabilityFilters] = useState(() => ({ ...DEFAULT_PERSONNEL_AVAILABILITY_FILTERS }));
  const [dutyWindow, setDutyWindow] = useState(null);
  const [activePreset, setActivePreset] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [expanded, setExpanded] = useState({}); // `${type}:${id}` -> bool
  const notificationVariant = variant === 'notification';
  const recipientScopeRestricted = [allowedUserIds, allowedDepartmentIds, allowedTeamIds, allowedCrewIds]
    .some(Array.isArray);
  const allowedUserSet = useMemo(
    () => (recipientScopeRestricted ? new Set((allowedUserIds || []).map(String)) : null),
    [allowedUserIds, recipientScopeRestricted]
  );
  const allowedDepartmentSet = useMemo(
    () => (recipientScopeRestricted ? new Set((allowedDepartmentIds || []).map(String)) : null),
    [allowedDepartmentIds, recipientScopeRestricted]
  );
  const allowedTeamSet = useMemo(
    () => (recipientScopeRestricted ? new Set((allowedTeamIds || []).map(String)) : null),
    [allowedTeamIds, recipientScopeRestricted]
  );
  const allowedCrewSet = useMemo(
    () => (recipientScopeRestricted ? new Set((allowedCrewIds || []).map(String)) : null),
    [allowedCrewIds, recipientScopeRestricted]
  );
  const groupScopeRestricted = allowedTeamSet !== null || allowedCrewSet !== null;
  const useIncidentScopedDirectory = Boolean(incidentId && recipientScopeRestricted);
  const userAllowed = useCallback(
    (id) => allowedUserSet === null || allowedUserSet.has(String(id)),
    [allowedUserSet]
  );
  const departmentAllowed = useCallback(
    (department) => allowedDepartmentSet === null
      || allowedDepartmentSet.has(String(department?.id || ''))
      || allowedDepartmentSet.has(String(department?.department_name || '')),
    [allowedDepartmentSet]
  );
  const crewAllowed = useCallback(
    (crew) => !groupScopeRestricted
      || allowedCrewSet?.has(String(crew?.id || ''))
      || allowedTeamSet?.has(String(crew?.team_id || '')),
    [allowedCrewSet, allowedTeamSet, groupScopeRestricted]
  );
  const refreshDirectory = useCallback((force = false) => {
    return loadDirectory({
      incidentId,
      restricted: useIncidentScopedDirectory,
      force,
    }).then(setDir);
  }, [incidentId, useIncidentScopedDirectory]);

  const refreshIncidentReadiness = useCallback(async ({ background = false } = {}) => {
    if (!incidentId) {
      setIncidentReadiness({});
      setReadinessLoadState('loaded');
      return;
    }
    if (!background) setReadinessLoadState('loading');
    try {
      const latest = await CommanServices.getIncidentReadinessCheck(incidentId);
      if (latest?.status === false) throw new Error(latest?.message || 'Unable to load readiness');
      const checkId = latest?.data?.id;
      if (!checkId) {
        setIncidentReadiness({});
        setReadinessLoadState('loaded');
        return;
      }
      const response = await CommanServices.getDutyCheckResponses(checkId);
      if (response?.status === false) throw new Error(response?.message || 'Unable to load readiness responses');
      const next = {};
      (response?.data?.rows || []).forEach((row) => {
        if (row?.user_id) next[String(row.user_id)] = row;
      });
      setIncidentReadiness(next);
      setReadinessLoadState('loaded');
    } catch (_error) {
      if (!background) {
        setIncidentReadiness({});
        setReadinessLoadState('unavailable');
      }
    }
  }, [incidentId]);

  useEffect(() => {
    refreshDirectory();
    refreshIncidentReadiness();
  }, [refreshDirectory, refreshIncidentReadiness]);

  useEffect(() => {
    const refresh = () => {
      refreshDirectory(true);
      refreshIncidentReadiness({ background: true });
    };
    window.addEventListener(COMPLETE_EVENT, refresh);
    const interval = setInterval(refresh, POLL_DUTY_MS);
    return () => {
      window.removeEventListener(COMPLETE_EVENT, refresh);
      clearInterval(interval);
    };
  }, [refreshDirectory, refreshIncidentReadiness]);

  const dutyInfoOf = (id) => dir.duty[String(id)] || {};
  const readinessInfoOf = (id) => incidentId ? (incidentReadiness[String(id)] || null) : undefined;
  const onlineSet = useMemo(() => {
    if (onlineUserIds instanceof Set) return onlineUserIds;
    return new Set(Array.isArray(onlineUserIds) ? onlineUserIds.map(String) : []);
  }, [onlineUserIds]);
  const hasPresenceContext = onlineUserIds !== null || Object.keys(presenceByUserId || {}).length > 0;
  const presenceInfoOf = (id) => (
    presenceByUserId?.[String(id)]
    || (onlineSet.has(String(id)) ? { user_id: String(id), online: true } : null)
  );
  const passesAvailabilityFilters = (id) => {
    if (!passesPersonnelAvailabilityFilters({
      presence: presenceInfoOf(id),
      dutyInfo: dutyInfoOf(id),
      readinessInfo: readinessInfoOf(id),
      readinessLoadState,
      filters: availabilityFilters,
    })) return false;
    if (availabilityFilters.duty === 'in_duty' && dutyWindow != null) {
      const at = parseTimestamp(dir.duty[String(id)]?.responded_at);
      if (!at || Number.isNaN(at.getTime())) return false;
      return (Date.now() - at.getTime()) <= dutyWindow * 60000;
    }
    return true;
  };
  const q = search.trim().toLowerCase();
  const match = (v) => !q || String(v || '').toLowerCase().includes(q);

  const involvedSet = useMemo(
    () => (involvedUserIds ? new Set(involvedUserIds.map(String)) : null),
    [involvedUserIds]
  );
  const rows = useMemo(() => {
    if (tab === 'users') {
      return dir.users
        .filter((u) => userAllowed(u.id) && u.is_active !== false && passesAvailabilityFilters(u.id) && (match(u.name) || match(u.email)))
        .sort((a, b) => (
          dutyRank(dutyInfoOf(a.id)) - dutyRank(dutyInfoOf(b.id))
          || readinessRank(readinessInfoOf(a.id), readinessLoadState) - readinessRank(readinessInfoOf(b.id), readinessLoadState)
          || usableReadinessDistance(readinessInfoOf(a.id)) - usableReadinessDistance(readinessInfoOf(b.id))
        ))
        .map((u) => ({ type: 'user', id: u.id, name: u.name || u.email, email: u.email || '', duty: dir.duty[String(u.id)]?.status, involved: !!involvedSet?.has(String(u.id)) }));
    }
    const groupRow = (type, id, name) => {
      const members = membersOfGroup(dir, type, id).filter((u) => userAllowed(u.id) && passesAvailabilityFilters(u.id));
      // A group counts as involved when any of its members is on the incident.
      const involved = !!involvedSet && membersOfGroup(dir, type, id).some((u) => involvedSet.has(String(u.id)));
      return { type, id, name, members, involved };
    };
    if (tab === 'departments') {
      return dir.departments
        .filter(departmentAllowed)
        .map((d) => groupRow('department', d.id, d.department_name))
        .filter((d) => match(d.name) || d.members.some((m) => match(m.name) || match(m.email)));
    }
    return dir.crews
      .filter(crewAllowed)
      .map((c) => groupRow('crew', c.id, `${c.crew_name} — ${c.team_name}`))
      .filter((c) => match(c.name) || c.members.some((m) => match(m.name) || match(m.email)));
  }, [tab, q, dir, availabilityFilters, dutyWindow, involvedSet, incidentId, incidentReadiness, readinessLoadState, onlineSet, presenceByUserId, userAllowed, departmentAllowed, crewAllowed]);

  // With involvedUserIds provided, a scope toggle (above the tabs) switches the
  // list between people/groups already involved and everyone in the system.
  const [scope, setScope] = useState('involved'); // involved | all
  const scopedRows = useMemo(() => {
    if (!involvedSet) return rows;
    return rows.filter((r) => (scope === 'involved' ? r.involved : !r.involved));
  }, [rows, involvedSet, scope]);
  const involvedCount = useMemo(() => (involvedSet ? rows.filter((r) => r.involved).length : 0), [rows, involvedSet]);
  const sections = [{ header: null, rows: scopedRows }];
  const hasActiveAvailabilityFilters = availabilityFilters.app !== 'all'
    || availabilityFilters.seenWithin != null
    || availabilityFilters.duty !== 'all'
    || availabilityFilters.incident !== 'all'
    || availabilityFilters.gps !== 'all'
    || availabilityFilters.maxDistance != null
    || availabilityFilters.maxEta != null
    || dutyWindow != null;
  const activeAvailabilityFilterCount = [
    availabilityFilters.app !== 'all',
    availabilityFilters.seenWithin != null,
    availabilityFilters.duty !== 'all',
    availabilityFilters.incident !== 'all',
    availabilityFilters.gps !== 'all',
    availabilityFilters.maxDistance != null,
    availabilityFilters.maxEta != null,
    dutyWindow != null,
  ].filter(Boolean).length;

  const resetAvailabilityFilters = () => {
    setAvailabilityFilters({ ...DEFAULT_PERSONNEL_AVAILABILITY_FILTERS });
    setDutyWindow(null);
    setActivePreset(null);
  };

  const applyNotificationPreset = (preset) => {
    if (activePreset === preset.id) {
      resetAvailabilityFilters();
      return;
    }
    setAvailabilityFilters({
      ...DEFAULT_PERSONNEL_AVAILABILITY_FILTERS,
      ...preset.filters,
    });
    setDutyWindow(null);
    setActivePreset(preset.id);
  };

  const keyOf = (r) => `${r.type}:${r.id}`;
  const selected = selections || [];
  useEffect(() => {
    if (allowedUserSet === null && allowedDepartmentSet === null && !groupScopeRestricted) return;
    const permitted = selected.filter((selection) => {
      if (selection.type === 'user') return userAllowed(selection.id);
      if (selection.type === 'department') {
        return departmentAllowed({ id: selection.id, department_name: selection.name });
      }
      if (selection.type === 'crew') {
        if (allowedCrewSet?.has(String(selection.id))) return true;
        const crew = dir.crews.find((row) => String(row.id) === String(selection.id));
        return crew ? crewAllowed(crew) : dir.crews.length === 0;
      }
      return false;
    });
    if (permitted.length !== selected.length) onChange(permitted);
  }, [selected, onChange, dir.crews, allowedUserSet, allowedDepartmentSet, allowedCrewSet, groupScopeRestricted, userAllowed, departmentAllowed, crewAllowed]);
  const isSelected = (r) => selected.some((s) => keyOf(s) === keyOf(r));
  const toggle = (r) => {
    if (isSelected(r)) onChange(selected.filter((s) => keyOf(s) !== keyOf(r)));
    else onChange([...selected, { type: r.type, id: r.id, name: r.name, role: '', sla: '' }]);
  };
  const setField = (r, field, value) => {
    onChange(selected.map((s) => (keyOf(s) === keyOf(r) ? { ...s, [field]: value } : s)));
  };
  // Add a group's (filtered) members as INDIVIDUAL user selections.
  const addAllMembers = (g) => {
    const additions = g.members
      .filter((u) => !selected.some((s) => s.type === 'user' && String(s.id) === String(u.id)))
      .map((u) => ({ type: 'user', id: u.id, name: u.name || u.email, role: '', sla: '' }));
    if (additions.length) onChange([...selected, ...additions]);
  };

  const selectAllVisible = () => {
    const additions = scopedRows
      .filter((row) => !isSelected(row))
      .map((row) => ({ type: row.type, id: row.id, name: row.name, role: '', sla: '' }));
    if (additions.length) onChange([...selected, ...additions]);
  };

  const clearAllSelections = () => {
    if (selected.length) onChange([]);
  };

  const renderNotificationPersonRow = (person, { memberOf = null } = {}) => {
    const picked = isSelected(person);
    const presenceMeta = mobilePresenceMeta(presenceInfoOf(person.id));
    const dutyMeta = dutyStatusMeta(dutyInfoOf(person.id));
    const rawReadinessMeta = readinessStatusMeta(readinessInfoOf(person.id), readinessLoadState);
    const location = readinessLocation(readinessInfoOf(person.id));
    const metadata = [
      hasPresenceContext && !presenceMeta.online ? presenceMeta.label : null,
      dutyMeta.status === 'no_response' ? 'No duty response' : dutyMeta.label,
      rawReadinessMeta.status === 'no_response'
        ? 'No incident response'
        : rawReadinessMeta.status === 'loading'
          ? 'Checking incident readiness'
          : rawReadinessMeta.status === 'unavailable_status'
            ? 'Incident status unavailable'
            : null,
      incidentId ? location.text : null,
    ].filter(Boolean);
    const readinessMeta = rawReadinessMeta.status === 'in_duty'
      ? { label: 'Ready', tone: 'ready' }
      : rawReadinessMeta.status === 'pending'
        ? { label: 'Pending', tone: 'pending' }
        : rawReadinessMeta.status === 'not_in_duty'
          ? { label: 'Unavailable', tone: 'danger' }
          : { label: 'Not checked', tone: 'muted' };
    const presenceDotClass = presenceMeta.online
      ? 'bg-emerald-500'
      : dutyMeta.status === 'in_duty'
        ? 'bg-amber-500'
        : 'bg-slate-400';
    const readinessClass = READINESS_BADGE_CLASSES[readinessMeta.tone] || READINESS_BADGE_CLASSES.muted;
    const rowTestId = memberOf
      ? `${testIdPrefix}-member-${memberOf}-${person.id}`
      : `${testIdPrefix}-person-${person.id}`;

    return (
      <label
        key={`${memberOf || 'person'}:${person.id}`}
        data-testid={rowTestId}
        className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors ${picked
          ? 'border-violet-200 bg-violet-50'
          : 'border-slate-200 bg-white hover:border-violet-200 hover:bg-violet-50/40'}`}
      >
        <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-700">
          {String(person.name || person.email || '?').trim().charAt(0).toUpperCase() || '?'}
          <span
            className={`absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white ${presenceDotClass}`}
            title={presenceMeta.label}
            aria-label={presenceMeta.label}
          />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-slate-900">{person.name || person.email}</span>
            {incidentId && (
              <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide ${readinessClass}`}>
                {readinessMeta.label}
              </span>
            )}
          </span>
          <span className="mt-0.5 block truncate text-[11px] text-slate-500">
            {metadata.length ? metadata.join(' · ') : 'Availability not reported'}
          </span>
        </span>
        <Checkbox
          checked={picked}
          onCheckedChange={() => toggle(person)}
          aria-label={`${picked ? 'Remove' : 'Select'} ${person.name || person.email}`}
          className="shrink-0"
        />
      </label>
    );
  };

  const roleSlaFields = (r) => (
    <div className="flex items-center gap-2 pl-9 pr-3 py-1.5 bg-blue-50/40" onClick={(e) => e.stopPropagation()}>
      <input
        value={selected.find((s) => keyOf(s) === keyOf(r))?.role || ''}
        onChange={(e) => setField(r, 'role', e.target.value)}
        placeholder="Role (optional)"
        data-testid={`${testIdPrefix}-role-${r.id}`}
        className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 bg-white"
      />
      <input
        type="number" min="0"
        value={selected.find((s) => keyOf(s) === keyOf(r))?.sla || ''}
        onChange={(e) => setField(r, 'sla', e.target.value)}
        placeholder="SLA min (optional)"
        data-testid={`${testIdPrefix}-sla-${r.id}`}
        className="w-32 text-xs border border-gray-300 rounded px-2 py-1 bg-white"
      />
    </div>
  );

  return (
    <div data-testid={`${testIdPrefix}-recipient-picker`}>
      {/* Scope toggle — only when the caller says who's already involved */}
      {involvedSet && (
        <div className="grid grid-cols-2 gap-1 rounded-lg bg-gray-100 p-1 mb-2" data-testid={`${testIdPrefix}-scope-toggle`}>
          {[
            { id: 'involved', label: `Involved in this incident (${involvedCount})` },
            { id: 'all', label: `All in the system (${rows.length - involvedCount})` },
          ].map((s) => (
            <button
              key={s.id}
              type="button"
              data-testid={`${testIdPrefix}-scope-${s.id}`}
              onClick={() => setScope(s.id)}
              className={`px-2 py-1.5 rounded-md text-[11px] font-bold transition-colors ${scope === s.id
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
      {notificationVariant ? (
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search people, departments, or teams…"
              data-testid={`${testIdPrefix}-search`}
              className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
            />
          </div>

          <div className="grid grid-cols-3 gap-1 rounded-lg bg-slate-100 p-1" role="tablist" aria-label="Recipient type">
            {[
              { id: 'users', label: 'Users' },
              { id: 'departments', label: 'Departments' },
              { id: 'crews', label: 'Teams/Crews' },
            ].map((target) => (
              <button
                key={target.id}
                type="button"
                role="tab"
                aria-selected={tab === target.id}
                onClick={() => setTab(target.id)}
                className={`rounded-md px-2 py-2 text-xs font-bold transition-colors ${tab === target.id
                  ? 'bg-white text-violet-700 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'}`}
                data-testid={`${testIdPrefix}-tab-${target.id}`}
              >
                {target.label}
              </button>
            ))}
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-1.5">
              {NOTIFICATION_PRESETS.map((preset) => {
                const selectedPreset = activePreset === preset.id;
                const smartIdle = preset.id === 'smart' && !selectedPreset;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    aria-pressed={selectedPreset}
                    data-testid={`${testIdPrefix}-preset-${preset.id}`}
                    onClick={() => applyNotificationPreset(preset)}
                    className={`rounded-full border px-3 py-1.5 text-[10px] font-bold transition-colors ${selectedPreset
                      ? 'border-violet-600 bg-violet-600 text-white'
                      : smartIdle
                        ? 'border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-violet-200 hover:text-violet-700'}`}
                  >
                    {preset.label}
                  </button>
                );
              })}
              <button
                type="button"
                aria-expanded={showAdvancedFilters}
                data-testid={`${testIdPrefix}-advanced-toggle`}
                onClick={() => setShowAdvancedFilters((current) => !current)}
                className="ml-auto inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] font-bold text-slate-600 hover:bg-slate-100 hover:text-violet-700"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Filters
                {activeAvailabilityFilterCount > 0 && (
                  <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-violet-600 px-1.5 py-0.5 text-[9px] text-white">
                    {activeAvailabilityFilterCount}
                  </span>
                )}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {showAdvancedFilters && (
              <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`${testIdPrefix}-advanced-panel`}>
                <PersonnelAvailabilityFilters
                  value={availabilityFilters}
                  onChange={(next) => {
                    setAvailabilityFilters(next);
                    setActivePreset(null);
                  }}
                  showApp={false}
                  showSeen={hasPresenceContext}
                  showDuty={false}
                  showIncident={false}
                  showGps={!!incidentId}
                  showDistance={!!incidentId}
                  showEta={!!incidentId}
                  testIdPrefix={`${testIdPrefix}-advanced`}
                />
                <button
                  type="button"
                  onClick={resetAvailabilityFilters}
                  disabled={!hasActiveAvailabilityFilters}
                  className="mt-2 text-[11px] font-bold text-violet-700 hover:text-violet-900 disabled:cursor-not-allowed disabled:text-slate-400"
                  data-testid={`${testIdPrefix}-reset-filters`}
                >
                  Reset filters
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between text-[11px]">
            <span className="font-medium text-slate-500">
              {scopedRows.length} selectable
            </span>
            <span className="inline-flex items-center gap-1.5 font-bold">
              <button
                type="button"
                onClick={selectAllVisible}
                disabled={scopedRows.length === 0 || scopedRows.every(isSelected)}
                className="text-violet-700 hover:text-violet-900 disabled:cursor-not-allowed disabled:text-slate-400"
                data-testid={`${testIdPrefix}-select-all`}
              >
                Select all
              </button>
              <span className="text-slate-300">/</span>
              <button
                type="button"
                onClick={clearAllSelections}
                disabled={selected.length === 0}
                className="text-violet-700 hover:text-violet-900 disabled:cursor-not-allowed disabled:text-slate-400"
                data-testid={`${testIdPrefix}-clear-all`}
              >
                Clear all
              </button>
            </span>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1 mb-2">
            {['users', 'departments', 'crews'].map((t) => (
              <button key={t} type="button" onClick={() => setTab(t)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md capitalize ${tab === t ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                data-testid={`${testIdPrefix}-tab-${t}`}>
                {t}
              </button>
            ))}
            <div className="relative flex-1 ml-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${tab}…`}
                data-testid={`${testIdPrefix}-search`}
                className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-md text-sm" />
            </div>
          </div>

          <PersonnelAvailabilityFilters
            value={availabilityFilters}
            onChange={(next) => {
              setAvailabilityFilters(next);
              if (next.duty !== 'in_duty') setDutyWindow(null);
            }}
            showApp={hasPresenceContext}
            showSeen={hasPresenceContext}
            showIncident={!!incidentId}
            showGps={!!incidentId}
            showDistance={!!incidentId}
            showEta={!!incidentId}
            testIdPrefix={testIdPrefix}
            className="mb-1.5"
          />
          {availabilityFilters.duty === 'in_duty' && (
            <div className="flex flex-wrap items-center gap-1 mb-1.5" data-testid={`${testIdPrefix}-duty-window`}>
              <span className="text-[10px] font-semibold text-gray-500">On duty within:</span>
              {DUTY_WINDOWS.map((w) => (
                <button key={String(w.value)} type="button"
                  data-testid={`${testIdPrefix}-duty-window-${w.value == null ? 'any' : w.value}`}
                  onClick={() => setDutyWindow(w.value)}
                  className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${dutyWindow === w.value
                    ? 'bg-emerald-600 border-emerald-600 text-white'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                  {w.label}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      <div className={notificationVariant
        ? 'mt-3 max-h-72 space-y-1.5 overflow-y-auto rounded-lg bg-slate-50/70 p-1.5'
        : 'border border-gray-200 rounded-md max-h-64 overflow-y-auto divide-y'}>
        {scopedRows.length === 0 && (
          <p className="p-3 text-xs text-gray-500">
            Nothing found{hasActiveAvailabilityFilters ? ' for these availability filters' : ''}{involvedSet && scope === 'involved' ? ' — switch to "All in the system" to add new people' : ''}.
          </p>
        )}
        {sections.map((section, si) => (
          <React.Fragment key={section.header || si}>
            {section.header && rows.length > 0 && (
              <div className="px-3 py-1.5 bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 sticky top-0" data-testid={`${testIdPrefix}-section-${si}`}>
                {section.header}
              </div>
            )}
            {section.header && section.rows.length === 0 && rows.length > 0 && (
              <p className="px-3 py-2 text-[11px] text-gray-400">{section.empty}</p>
            )}
            {section.rows.map((r) => {
          if (r.type === 'user') {
            if (notificationVariant) {
              return (
                <React.Fragment key={keyOf(r)}>
                  {renderNotificationPersonRow(r)}
                  {isSelected(r) && roleSlaFields(r)}
                </React.Fragment>
              );
            }
            return (
              <React.Fragment key={keyOf(r)}>
                <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                  <Checkbox checked={isSelected(r)} onCheckedChange={() => toggle(r)} />
                  <span className="flex-1">{r.name}</span>
                  <PersonnelAvailabilitySummary
                    presence={presenceInfoOf(r.id)}
                    dutyInfo={dutyInfoOf(r.id)}
                    readinessInfo={readinessInfoOf(r.id)}
                    readinessLoadState={readinessLoadState}
                    showPresence={hasPresenceContext}
                    showIncident={!!incidentId}
                    showLocation={!!incidentId}
                    testId={`${testIdPrefix}-availability-${r.id}`}
                  />
                </label>
                {isSelected(r) && roleSlaFields(r)}
              </React.Fragment>
            );
          }
          // Department / crew row: group checkbox + expandable member list + Add all
          const key = keyOf(r);
          const isOpen = !!expanded[key];
          const Icon = r.type === 'department' ? Building2 : Shield;
          return (
            <React.Fragment key={key}>
              <div className={`flex items-center gap-2 text-sm ${notificationVariant
                ? `rounded-lg border px-3 py-2.5 transition-colors ${isSelected(r) ? 'border-violet-200 bg-violet-50' : 'border-slate-200 bg-white hover:border-violet-200'}`
                : 'px-3 py-2 hover:bg-gray-50'}`}>
                {!notificationVariant && (
                  <Checkbox checked={isSelected(r)} onCheckedChange={() => toggle(r)} title={`Select the whole ${r.type} as one recipient`} />
                )}
                <button type="button" className="flex items-center gap-2 flex-1 min-w-0 text-left"
                  data-testid={`${testIdPrefix}-group-expand-${r.id}`}
                  onClick={() => setExpanded((p) => ({ ...p, [key]: !p[key] }))}>
                  <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                  <Icon className={`h-3.5 w-3.5 shrink-0 ${r.type === 'department' ? 'text-indigo-500' : 'text-teal-600'}`} />
                  <span className={`truncate ${notificationVariant ? 'font-semibold text-slate-800' : ''}`}>{r.name}</span>
                </button>
                <span className="text-[10px] font-bold text-gray-400 shrink-0">{r.members.length} {notificationVariant ? 'selectable' : `member${r.members.length === 1 ? '' : 's'}`}</span>
                {notificationVariant && (
                  <Checkbox
                    checked={isSelected(r)}
                    onCheckedChange={() => toggle(r)}
                    title={`Select the whole ${r.type} as one recipient`}
                    aria-label={`Select ${r.name}`}
                  />
                )}
                <button type="button" disabled={r.members.length === 0}
                  data-testid={`${testIdPrefix}-group-all-${r.id}`}
                  onClick={() => addAllMembers(r)}
                  className="shrink-0 rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40">
                  Add all
                </button>
              </div>
              {isSelected(r) && roleSlaFields(r)}
              {isOpen && (
                <div className={notificationVariant ? 'ml-6 space-y-1.5 rounded-lg bg-slate-100/70 p-2' : 'bg-gray-50/60 px-1 py-1'}>
                  {r.members.map((u) => {
                    const asUser = { type: 'user', id: u.id, name: u.name || u.email };
                    const picked = selected.some((s) => s.type === 'user' && String(s.id) === String(u.id));
                    if (notificationVariant) {
                      return renderNotificationPersonRow(asUser, { memberOf: r.id });
                    }
                    return (
                      <button key={u.id} type="button"
                        data-testid={`${testIdPrefix}-member-${r.id}-${u.id}`}
                        onClick={() => toggle(asUser)}
                        className={`w-full flex items-center gap-2 rounded px-2.5 py-1.5 text-left text-xs ${picked ? 'bg-indigo-50 text-indigo-800' : 'hover:bg-indigo-50 text-gray-700'}`}>
                        {picked ? <span className="h-3 w-3 shrink-0 rounded-sm bg-indigo-600" /> : <Plus className="h-3 w-3 shrink-0 text-indigo-500" />}
                        <UserCircle2 className="h-3.5 w-3.5 shrink-0 text-blue-500" />
                        <span className="flex-1 truncate">{u.name || u.email}</span>
                        <PersonnelAvailabilitySummary
                          presence={presenceInfoOf(u.id)}
                          dutyInfo={dutyInfoOf(u.id)}
                          readinessInfo={readinessInfoOf(u.id)}
                          readinessLoadState={readinessLoadState}
                          showPresence={hasPresenceContext}
                          showIncident={!!incidentId}
                          showLocation={!!incidentId}
                          testId={`${testIdPrefix}-member-availability-${r.id}-${u.id}`}
                        />
                      </button>
                    );
                  })}
                  {r.members.length === 0 && <p className="px-2.5 py-1.5 text-[11px] text-gray-400">No members{hasActiveAvailabilityFilters ? ' match these availability filters' : ''}.</p>}
                </div>
              )}
            </React.Fragment>
          );
        })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// Flatten selections to individual users (departments/crews resolved to their
// CURRENT members), each carrying the selection's role/sla.
export const resolveRecipients = async (selections, { incidentId = null, restricted = false } = {}) => {
  const dir = await loadDirectory({ incidentId, restricted });
  const out = new Map();
  for (const s of selections || []) {
    if (s.type === 'user') {
      const u = dir.users.find((x) => String(x.id) === String(s.id));
      if (u) out.set(String(u.id), { userId: u.id, userName: u.name || u.email, email: u.email || '', role: s.role || '', sla: s.sla || '', sourceType: null, sourceName: null });
      continue;
    }
    for (const u of membersOfGroup(dir, s.type, s.id)) {
      if (!out.has(String(u.id))) {
        out.set(String(u.id), { userId: u.id, userName: u.name || u.email, email: u.email || '', role: s.role || '', sla: s.sla || '', sourceType: s.type, sourceName: s.name });
      }
    }
  }
  return [...out.values()];
};

export default RecipientPicker;
